import { merge } from '../../helper';

import insertAfter from '../../general/insertAfter';
import prependChild from '../../general/prepend/prependChild';

import type { Shared, Context, ContextDraft, IManagerConfig, IManagerApi } from './types';
import inCurrent from './modules/current';
import inSelect from './modules/select';
import inText from './modules/text';
import inListing from './modules/listing';
import inDropdown from './modules/dropdown';
import inSearch from './modules/search';
import inProgress from './modules/progress';
import inAddOptions from './modules/addOption';
import inRequest from './modules/request';
import inOption from './modules/option';
import inRead from './modules/read';
import inHelpers from './modules/helpers';

// ----------------------------------------------------------------------------

function createShared(): Shared {
    return {
        elements: {},
        state: {},
        config: {
            type: 'single',
            select: {
                isTextFormat: true,
            },
            search: {
                isHighlight: true,
            },
        },
        events: {
            change: 'select-change',
        },
    } as Shared;
}

function mountElements(shared: Shared): void {
    insertAfter(shared.elements.element, shared.elements.current);
    prependChild(shared.elements.element, shared.elements.current);
}

function manager(element: HTMLSelectElement, config: IManagerConfig = {}): IManagerApi {
    if (!(element instanceof HTMLSelectElement)) {
        throw new Error('Element is not a HTMLSelectElement');
    }

    const ctx: ContextDraft = {
        shared: createShared(),
    };
    const ctxModule = ctx as Context;
    const shared = ctx.shared;

    const onResize = (): void => {
        ctxModule.dropdown.positionDropdown();
    };

    ctx.current = inCurrent(element, ctxModule);
    ctx.select = inSelect(ctxModule);
    ctx.text = inText(ctxModule);
    ctx.dropdown = inDropdown(ctxModule);
    ctx.listing = inListing(ctxModule);
    ctx.option = inOption(ctxModule);
    ctx.helpers = inHelpers(ctxModule);

    ctx.addOption = inAddOptions(ctxModule);
    ctx.progress = inProgress(ctxModule);
    ctx.request = inRequest(ctxModule);
    ctx.search = inSearch(ctxModule);
    ctx.read = inRead(ctxModule);

    // ------------------------------------------------------------------------

    function init(): void {
        merge(shared.config, config);

        //
        start();

        //
        mountElements(shared);
    }

    // ------------------------------------------------------------------------

    function start(): void {
        ctxModule.current.init();
        ctxModule.select.init();
        ctxModule.text.init();
        ctxModule.dropdown.init();
        ctxModule.listing.init();

        // Optional
        ctxModule.search.init();
        ctxModule.progress.init();
        ctxModule.addOption.init();
        ctxModule.request.init();

        // Start
        ctxModule.read.start();

        window.addEventListener('resize', onResize);
    }

    function destroy(): void {
        window.removeEventListener('resize', onResize);
    }

    // ------------------------------------------------------------------------

    init(); // Ready

    return {
        getElement: ctxModule.select.getElement,
        getCurrent: ctxModule.current.getElement,
        selected: {
            has: ctxModule.helpers.hasSelected,
            get: ctxModule.helpers.getSelected,
            set: ctxModule.helpers.setSelected,
            clear: ctxModule.helpers.clearSelected,
        },
        options: {
            add: ctxModule.helpers.addOptions,
            reset: ctxModule.helpers.resetOptions,
        },
        request: {
            reload: ctxModule.request.reload,
        },
        enabled: ctxModule.helpers.enabled,
        disabled: ctxModule.helpers.disabled,
        reset: ctxModule.helpers.reset,
        clear: ctxModule.helpers.clear,
        destroy,
    };
}

// ----------------------------------------------------------------------------

export default manager;
